import React, { useCallback, useEffect, useMemo } from "react";

import cx from 'classnames';

import Slider from "src/components/slider";
import Button from "src/components/button";
import { Asset, AssetFile, AssetUpload } from "src/types/assets";

import styles from "./add.module.scss";
import { AddAssetsFromHub } from "./hub";
import { animated, Spring, useTransition } from "@react-spring/web";
import _ from "lodash";
import { Collection } from "src/types/collections";
import { AssetCard, AssetUploadCard, CleverstoryAssetCard, LinkAssetCard } from "./card";
import AddUrlAsset from "./components/links";
import AddAssetFromCleverStory from "./components/cleverstory";

import Dropzone, { DropzoneRef } from "react-dropzone";
import { nanoid } from "nanoid";
import { FileService } from "src/services/file.service";
import S3Upload from "react-s3-uploader/s3upload";
import AuthService from "src/services/auth.service";
import FileUploadService from "src/services/file.upload.service";
import { useDispatch } from "react-redux";
import { createAssetFromFile, invalidateAssetUpload, invalidateLinkAsset, toggleAssetStatusInContentSimilarityQueue, uploadAsset, uploadAssetFailed, uploadAssetProgress, uploadingAssetSucceeded } from "src/redux/actions/assets";
import { useSelector } from "react-redux";
import { fetchActivatedWorkSpaces, invalidateCleverstoryAssets } from "src/redux/actions/cleverstory";
import { useAssetAdoption } from "src/redux/actions/adoption/asset";
import { Group } from "src/types/groups";
import { Stream } from "src/types/streams";
import { Course } from "src/types/coaching/courses";
import { Podcast } from "src/types/coaching/podcast";

// eslint-disable-next-line no-undef
const apiUrl = `${process.env.REACT_APP_API_URL}/api/2.0`;

interface AssetState {
  option: 'hub-assets' | 'cleverstory-assets' | null;
  entities: (Asset | Collection | any)[];
}

interface SourceProps {
  type: 'collection' | 'stream' | 'group' | 'seek' | 'compose' | 'course' | 'podcast';
  entity: Collection | Stream | Group | Course | Podcast | null;
}

interface AddAssetsDialogProps {
  show: boolean;
  selectableEntityTypes: string[];
  source?: SourceProps;
  allowBackgroundUpload?: boolean;
  // eslint-disable-next-line no-unused-vars
  selectionFilter?: (entity: any) => boolean;
  // eslint-disable-next-line no-unused-vars
  entityAction?: (entity: any) => any;
  // eslint-disable-next-line no-unused-vars
  onFinish: (entities: (Asset | AssetUpload | Collection)[]) => any;

  onClose: () => void;
}

const AddAssetsDialog: React.FC<AddAssetsDialogProps> = ({ show, selectableEntityTypes, source, selectionFilter, entityAction, onFinish, onClose, allowBackgroundUpload=true }) => {
  const dispatch = useDispatch();

  const { trackAddAssetScreenView, trackAssetAdded } = useAssetAdoption();

  const [state, setState] = React.useState<AssetState>({ option: null, entities: [] });

  const hasEntities = state.entities?.length > 0;

  const canFinish = useMemo(() => {
    if (allowBackgroundUpload && _.size(state.entities) > 0) {
      return true;
    }

    const entities = (state.entities || []).filter(entity => {
      return entity.entityType !== 'asset-upload';
    }).filter(entity => {
      if(entity.entityType === 'asset') {
        return _.get(entity, 'status') === "OK";
      }

      return true;
    });

    return _.size(entities) > 0;
  }, [state.entities, allowBackgroundUpload]);

  const newAssetsCreated = useSelector((reduxState) => {
    const uploadState = _.get(reduxState, 'asset.upload');

    const items = _.keys(uploadState).filter(key => {
      const upload = _.get(uploadState, `[${key}]`);
      return _.get(upload, 'created');
    }).map(key => {
      return _.get(uploadState, `[${key}]`);
    });

    return items;
  }, _.isEqual);

  const failedAssets = state.entities.filter(entity => entity != null && _.size(_.get(entity, "error.errors"))> 0);

  const newLinksCreated = useSelector((reduxState) => {
    const linksState = _.get(reduxState, 'asset.linkAsset');

    const items = _.keys(linksState).filter(key => {
      const upload = _.get(linksState, `[${key}]`);
      return _.get(upload, 'created');
    }).map(key => {
      return _.get(linksState, `[${key}]`);
    });

    return items;
  }, _.isEqual);

  const failedLinks = useSelector((reduxState) => {
    const linksState = _.get(reduxState, 'asset.linkAsset');

    const errorLinks = _.keys(linksState).filter(key => {
      const upload = _.get(linksState, `[${key}]`);
      return _.get(upload, 'error');
    }).map(key => {
      return _.get(linksState, `[${key}]`);
    });

    return errorLinks;
  }, _.isEqual);

  const newPagesCreated = useSelector((reduxState) => {
    const pagesState = _.get(reduxState, 'cleverStory.create');

    const items = _.keys(pagesState).filter(key => {
      const upload = _.get(pagesState, `[${key}]`);
      return _.get(upload, 'created');
    }).map(key => {
      return _.get(pagesState, `[${key}]`);
    });

    return items;
  }, _.isEqual);

  useEffect(() => {
    if (show) {
      trackAddAssetScreenView();
      dispatch(fetchActivatedWorkSpaces())
    }

    setState({
      option: null,
      entities: []
    });

  }, [show]);

  const handleStateChange = (state: AssetState) => {
    setState(state);
  }

  const handleClose = () => {

    invalidateNewEntities();
    (state.entities || []).forEach((entity) => {
      if (entity.entityType === 'asset-upload') {
        const uploader = FileUploadService.getUploader(entity.id);

        if (uploader) {
          console.log(`Aborting upload ${entity.id}`);
          uploader.abortUpload();
        }
        dispatch(invalidateAssetUpload(entity.id));
      }
    });

    onClose();
  }

  const invalidateNewEntities = () => {
    if (_.size(newAssetsCreated) > 0) {
      newAssetsCreated.forEach((asset: any) => {
        dispatch(invalidateAssetUpload(asset.id));
      });
    }

    if (_.size(newLinksCreated) > 0) {
      newLinksCreated.forEach((link: any) => {
        dispatch(invalidateLinkAsset(link.id));
      });
    }

    if (_.size(newPagesCreated) > 0) {
      newPagesCreated.forEach((page: any) => {
        dispatch(invalidateCleverstoryAssets(page.id));
      });
    }

    if (_.size(failedAssets) > 0) {
      failedAssets.forEach((asset: any) => {
        dispatch(invalidateAssetUpload(asset.id));
      });    
    }

    if (_.size(failedLinks) > 0) {
      failedLinks.forEach((link: any) => {
        dispatch(invalidateLinkAsset(link.id));
      });
    }
  }

  const handleFinish = () => {
    const result = onFinish(state.entities);
    if (result === false) {
      return;
    }

    if (source) {
      state.entities.forEach(item => {
        const type = FileService.getFileType(item.metadata);
        let sourceType: 'file' | 'link' | 'cleverstory' | 'existing_assets' = 'existing_assets';

        if (type === 'INTERACTIVE') {
          const page = _.find(newPagesCreated, (a) => { return _.get(a, 'item.id') === item.id; });
          if (page) {
            sourceType = "cleverstory";
          }
        } else if(type === 'LINK'){
          const link = _.find(newLinksCreated, (a) => { return _.get(a, 'item.id') === item.id; });
          if (link) {
            sourceType = "link";
          }
        } else {
          const asset = _.find(newAssetsCreated, (a) => { return _.get(a, 'item.id') === item.id; });
          if (asset) {
            sourceType = "file";
          }
        }

        trackAssetAdded({
          ...source,
          source: sourceType,
          asset: item,
        });
      });
    }

    const uploads = state.entities || [];
    if(_.size(uploads) > 0) {
      _.map(uploads, (item) => {
        if(state.option != "hub-assets"){
          dispatch(toggleAssetStatusInContentSimilarityQueue({asset: item, initialise: true}));
        }
      })
    }

    invalidateNewEntities();
    onClose();
  }

  return (
    <Slider.Dialog show={show} width={hasEntities ? "720px" : "380px"} className={styles.dialog} onClose={handleClose}>
      <Slider.Body className={styles.body}>
        <AddAssetsPanel state={state}
          selectableEntityTypes={selectableEntityTypes}
          source={source}
          allowBackgroundUpload={allowBackgroundUpload}
          entityAction={entityAction}
          selectionFilter={selectionFilter}
          onChange={handleStateChange}
          onClose={handleClose}
        />
      </Slider.Body>
      <Slider.Actions>
        <Button
          variant="primary"
          icon="paperflite-tick"
          text="Confirm"
          onClick={handleFinish}
          disabled={!canFinish}
        />
        <Button
          variant="secondary"
          text="Cancel"
          onClick={handleClose}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
};

interface AddAssetsPanelProps {
  state: AssetState;
  selectableEntityTypes: string[];
  source?: SourceProps;
  allowBackgroundUpload?: boolean;
  
  // eslint-disable-next-line no-unused-vars
  selectionFilter?: (entity: any) => boolean;
  // eslint-disable-next-line no-unused-vars
  entityAction?: (entity: any) => any;

  // eslint-disable-next-line no-unused-vars
  onChange: (state: AssetState) => void;
  onClose?: () => void;
}

const AddAssetsPanel: React.FC<AddAssetsPanelProps> = ({ state, selectableEntityTypes, source, allowBackgroundUpload, onChange, onClose, selectionFilter, entityAction}) => {
  return (  
    <div className={styles.assetPanel}>
      <AddAssets width={375} state={state}
        selectableEntityTypes={selectableEntityTypes}
        source={source}
        selectionFilter={selectionFilter}
        entityAction={entityAction}
        onStateChange={onChange}
        onClose={onClose}
      />
      <AssetSelection style={{}} state={state} onStateChange={onChange} />
    </div>
  );
}

interface AddAssetsProps {
  width: number;
  selectableEntityTypes: string[];
  state: AssetState;
  source?: SourceProps;

  // eslint-disable-next-line no-unused-vars
  selectionFilter?: (entity: any) => boolean;
  // eslint-disable-next-line no-unused-vars
  entityAction?: (entity: any) => any;

  onDropOver?: () => void;
  onDropOut?: () => void;
  // eslint-disable-next-line no-unused-vars
  onStateChange: (state: AssetState) => void;
  onClose?: () => void;
  className? : string;
}

const AddAssets: React.FC<AddAssetsProps> = ({ className, width, selectableEntityTypes, state, source, onStateChange, onClose, onDropOver, onDropOut, selectionFilter, entityAction }) => {
  const dispatch = useDispatch();

  const dropZoneRef = React.useRef<DropzoneRef>(null);

  const { trackAssetSelected } = useAssetAdoption();

  const showOptions = state.option === null;
  const showHub = state.option === 'hub-assets';
  const showCleverStory = state.option === 'cleverstory-assets';

  const [dragging, setDragging] = React.useState(false);

  const optionsTransition = useTransition(showOptions, {
    initial: { opacity: 1, transform: 'translateX(0%)' },
    from: { opacity: 0, transform: 'translateX(-100%)' },
    enter: { opacity: 1, transform: 'translateX(0%)' },
    leave: { opacity: 0, transform: 'translateX(-100%)' },
  });

  const hubTransition = useTransition(showHub, {
    initial: { opacity: 0, transform: 'translateX(100%)' },
    from: { opacity: 0, transform: 'translateX(100%)' },
    enter: { opacity: 1, transform: 'translateX(0%)' },
    leave: { opacity: 0, transform: 'translateX(100%)' },
  });

  const cleverStoryTransition = useTransition(showCleverStory, {
    initial: { opacity: 0, transform: 'translateX(100%)' },
    from: { opacity: 0, transform: 'translateX(100%)' },
    enter: { opacity: 1, transform: 'translateX(0%)' },
    leave: { opacity: 0, transform: 'translateX(100%)' },
  });

  const uploads = useSelector(reduxState => {
    let assets = state.entities || [];

    const uploadState = _.get(reduxState, 'asset.upload');
    const items = _.keys(uploadState).map(key => {
      const upload = _.get(uploadState, `[${key}]`);
      return upload;
    });

    const createdAssets = items.filter(asset => {
      const created = _.get(asset, 'created');
      return created;
    });

    const createdLinks = _.keys((_.get(reduxState, 'asset.linkAsset') || {})).map(key => {
      const link = _.get(reduxState, `asset.linkAsset[${key}]`);
      return link;
    }).filter(item => {
      return _.get(item, 'created');
    });

    const createdPages = _.keys((_.get(reduxState, 'cleverStory.create') || {})).map(key => {
      const page = _.get(reduxState, `cleverStory.create[${key}]`);
      return page;
    }).filter(item => {
      return _.get(item, 'created');
    });

    const uploadingAssets = items.filter(asset => {
      const created = _.get(asset, 'created');
      return !created;
    }).map((item: any) => {
      return {
        id: item.id,
        entityType: 'asset-upload',
        name: item.item.name,
        progress: _.get(item, 'item.progress', 0),
        error: _.get(item, "error", null)
      };
    });

    assets = assets.map(asset => {
      const createdAsset = createdAssets.find(item => (item as any).id === asset.id);

      if (createdAsset) {
        const item = _.get(createdAsset, 'item');
        return item;
      }

      const createdLink = createdLinks.find(item => (item as any).id === asset.id);

      if (createdLink) {
        const item = _.get(createdLink, 'item');
        return item;
      }

      if(createdPages){
        const createdPage = createdPages.find(item => item != null && (item as any).id === asset?.id);
        if (createdPage) {
          const item = _.get(createdPage, 'item');
          return item;
        }
      }

      if (createdLink) {
        const item = _.get(createdLink, 'item');
        return item;
      }

      const existingAsset = createdAssets.find(item => _.get(item, 'item.id') === asset.id);
      if (existingAsset) {
        const item = _.get(existingAsset, 'item');
        return item;
      }

      const existingLink = createdLinks.find(item => _.get(item, 'item.id') === asset.id);
      if (existingLink) {
        const item = _.get(existingLink, 'item');
        return item;
      }

      const existingPage = createdPages.find(item => item != null && _.get(item, 'item.id') === asset?.id);
      if (existingPage) {
        const item = _.get(existingPage, 'item');
        return item;
      }

      const uploadingAsset = uploadingAssets.find(item => item.id === asset.id);
      if (uploadingAsset) {
        return uploadingAsset
      }

      if (asset?.entityType === 'collection') {
        const collection = _.get(reduxState, `collections.collection[${asset.id}].item`);
        if (collection) {
          return collection;
        }
      }

      return asset;
    });

    const newUploads = items.filter((asset: any) => {
      return !_.includes(assets, { id: asset.id });
    }).filter(item => {
      return !_.get(item, 'created');
    });

    const links = _.keys((_.get(reduxState, 'asset.linkAsset') || {})).map(key => {
      const link = _.get(reduxState, `asset.linkAsset[${key}]`);
      return link;
    }).filter(item => {
      return !_.get(item, 'created');
    }).map((item: any) => {
      return {
        id: item.id,
        entityType: 'link',
        url: item.url,
      }
    });

    const allPages = _.keys(_.get(reduxState, 'cleverStory.create', {})).map(key => {
      return _.get(reduxState, `cleverStory.create[${key}]`);
    });

    let validPages = allPages.filter((item: any) => !item?.error);
    validPages = validPages.filter(item => !_.get(item, "created"));
    
    const pages = validPages.map((item: any) => ({
      id: item.id,
      entityType: 'cleverstory',
      item: item.page
    }));

    if (_.size(newUploads) > 0) {
      assets = [...assets, ...newUploads];
    }

    if (_.size(links) > 0) {
      assets = [...assets, ...links];
    }

    if (_.size(pages) > 0) {
      assets = [...assets, ...pages];
    }

    if (!assets) {
      assets = [];
    } else {
      assets = assets.filter(item => !_.isEmpty(item));
      assets = _.uniqBy(assets, 'id');
    }

    return assets;
  }, _.isEqual);


  useEffect(() => {
    if (_.size(uploads) > 0 && source) {
      uploads.filter(item => {
        return _.get(item, "entityType") === 'asset' && _.find(state.entities, { id: item.id }) == null;
      }).forEach(item => {
        let sourceType: 'file' | 'link' | 'cleverstory' | 'existing_assets' = 'file';

        const type = FileService.getFileType(item.metadata);
        if (type === 'INTERACTIVE') {
          sourceType = 'cleverstory';
        } else if (type === 'LINK') {
          sourceType = 'link';
        }

        trackAssetSelected({
          ...source,
          asset: item,
          source: sourceType
        });
      });
    }

    onStateChange({
      ...state,
      entities: uploads
    });

  }, [uploads]);

  const onDrop = useCallback((acceptedFiles) => {
    handleDropLeave();

    // Do something with the files
    acceptedFiles.forEach((file: any) => {
      uploadFile(file);
    });
  }, []);

  const handleDropEnter = () => {
    setDragging(true);

    if (onDropOver) {
      onDropOver();
    }
  }

  const handleDropLeave = () => {
    setDragging(false);

    if (onDropOut) {
      onDropOut();
    }
  }

  const handleDropOver = () => {
    setDragging(true);

    if (onDropOver) {
      onDropOver();
    }
  }

  const handleSelection = (entity: any) => {
    const canSelectEntity = _.includes(selectableEntityTypes, entity.entityType) &&
      (selectionFilter ? selectionFilter(entity) : true);

    if (canSelectEntity) {

      if (source && entity.entityType === 'asset') {
        trackAssetSelected({
          ...source,
          source: 'existing_assets',
          asset: entity as Asset
        });
      }

      const existing = state.entities || [];
      const differentEntities = existing.filter((item) => item.entityType !== entity.entityType);
      if (_.size(differentEntities) > 0) {
        onStateChange({
          ...state,
          entities: [entity]
        });

        return;
      }

      if (entity.entityType === 'asset') {
        let entities = state.entities || [];
        entities = [...entities, entity];
        entities = _.uniqBy(entities, 'id');
        onStateChange({
          ...state,
          entities: entities
        });
      } else if (entity.entityType === 'collection') {
        const isExistingCollection = existing.some(
          (item) => (item.entityType === 'collection') && (item.id === entity.id)
        );
  
        if (!isExistingCollection) {
          const newEntities = existing.filter((item) => item.entityType !== 'collection');
          onStateChange({
            ...state,
            entities: [...newEntities, entity] 
          });
        }
      }
    }
  }

  const uploadFile = (file) => {
    file.id = nanoid();
    const fileName = _.get(file, "name", "Untitled");
    let fileType = FileService.getFileExtension(fileName);
    if (file.type == "" || file.type == null || file.type == undefined) {
      fileType = "application/octet-stream";
      file = new File([file], fileName, { type: fileType })
      file.id = nanoid();
    }

    const s3Uploader = new S3Upload({
      signingUrl: "/file_upload/initiate",
      signingUrlMethod: "POST",
      signingUrlHeaders: {
        Authorization: `Bearer ${AuthService.getAccessToken()}`,
      },
      server: apiUrl,
      scrubFilename: (filename) => {
        const name = filename.replace(/[^\w\d_\-.\s]+/ig, "");
        return encodeURIComponent(name);
      },
      signingUrlQueryParams: { originalName: encodeURIComponent(fileName) },
      contentDisposition: "auto",
      uploadRequestHeaders: {},
      onFinishS3Put: handleFileUploadFinish,
      onProgress: handleFileUploadProgress,
      onError: handleFileUploadError,
      files: [file],
    });

    FileUploadService.trackUpload(s3Uploader);

    dispatch(uploadAsset({
      file: {
        id: file.id,
        name: fileName,
        type: fileType,
        size: file.size,
      } as AssetFile
    }));
  }

  const handleFileUploadError = (file, error) => {
    dispatch(uploadAssetFailed({
      id: file.id,
      error: error
    }));
  }

  const handleFileUploadProgress = (progress, status, file) => {
    dispatch(uploadAssetProgress({
      id: file.id,
      progress: progress
    }));
  }

  const handleFileUploadFinish = (data, file) => {
    FileUploadService.markFinish(file.id);
    dispatch(uploadingAssetSucceeded({
      id: file.id
    }));

    dispatch(createAssetFromFile({
      id: file.id,
      signedUrl: data.signedUrl,
    }));
  }

  const handleBrowseClicked = () => {
    if (dropZoneRef.current) {
      dropZoneRef.current.open();
    }
  }

  return (
    <Dropzone ref={dropZoneRef} noClick={true}
      onDrop={onDrop}
      onDragEnter={handleDropEnter}
      onDragLeave={handleDropLeave}
      onDragOver={handleDropOver}
    >
      {({ getRootProps, getInputProps }) => (
        <div className={styles.addAsset} style={{ width: width }}  {...getRootProps()}>
          <input {...getInputProps()} type="file" />
          <Header state={state} onStateChange={onStateChange} onClose={onClose} className={className}/>
          <div className={cx(styles.stacks, styles.content)}>
            {optionsTransition(
              (style, item) =>
                item && (
                  <AddAssetsOptions
                    style={style}
                    state={state}
                    source={source}
                    onStateChange={onStateChange}
                    onBrowse={handleBrowseClicked}
                  />
                ),
            )}

            {hubTransition(
              (style, item) =>
                item && (
                  <AddAssetsFromHub
                    style={style}
                    className ={className}
                    width={width}
                    selectableEntityTypes={selectableEntityTypes}
                    onSelection={handleSelection}
                    entityAction={entityAction}
                    source={source}
                  />
                ),
            )}

            {cleverStoryTransition(
              (style, item) =>
                item && (
                  <AddAssetFromCleverStory
                    style={style}
                    onSelection={handleSelection}
                    selectedAssets={state.entities}
                  />
                ),
            )}

            {dragging && <FileDropIndicator /> }
          </div>
        </div>
      )}
    </Dropzone>
  );
};

interface HeaderProps {
  state: AssetState;
  // eslint-disable-next-line no-unused-vars
  onStateChange: (state: AssetState) => void;
  onClose?: () => void;
  className?: string;
}

const Header: React.FC<HeaderProps> = ({ state, onStateChange, onClose,className }) => {
  const showPrimary = state.option === null;
  const showSecondary =
    state.option === "hub-assets" || state.option === "cleverstory-assets";

  const getTextForHeader = (textType) => {
    if (textType === "title") {
      switch (state.option) {
        case "hub-assets":
          return <h5 className={styles.title}>streams & collections</h5>;
        case "cleverstory-assets":
          return (
            <img
              height={28}
              width={110}
              src="/images/assets/add/cleverstory.png"
              alt="cleverstory"
            />
          );
        default:
          break;
      }
    } else if (textType === "subTitle") {
      switch (state.option) {
        case "hub-assets":
          return "Some of your best content is on the hub. Go get it!";
        case "cleverstory-assets":
          return "Add assets from Cleverstory storyboards";
        default:
          break;
      }
    }
  };

  const primaryTransition = useTransition(showPrimary, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const secondaryTransition = useTransition(showSecondary, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { tension: 120, friction: 14 },
  });

  const handleBackClicked = () => {
    onStateChange({ ...state, option: null });
  };

  return (
    <div className={cx(styles.header, className)}> 
          {primaryTransition(
        (style, item) =>
          item && (
            <animated.div className={styles.primary} style={style}>
              <div className={styles.row}>
                <h5 className={styles.title}>adding assets</h5>
                {onClose && (
                  <button className={styles.close} onClick={onClose}>
                    <i className="paperflite-close"></i>
                  </button>
                )}
              </div>
              <p className={styles.message}>&nbsp;</p>
            </animated.div>
          ),
      )}

      {secondaryTransition(
        (style, item) =>
          item && (
            <animated.div className={styles.secondary} style={style}>
              <div className={styles.row}>
                <Spring
                  to={{
                    opacity: 1,
                  }}
                  from={{
                    opacity: 0,
                  }}
                  config={{
                    duration: 300,
                  }}
                >
                  {(style) => (
                    <animated.button
                      style={style}
                      className={styles.back}
                      onClick={handleBackClicked}
                    >
                      <i className="paperflite-left-arrow"></i>
                    </animated.button>
                  )}
                </Spring>
                {getTextForHeader("title")}
              </div>
              <p className={styles.message}>{getTextForHeader("subTitle")}</p>
            </animated.div>
          ),
      )}
    </div>
  );
};

interface AddAssetsOptionProps {
  style: any;
  state: AssetState;
  source?: SourceProps;

  // eslint-disable-next-line no-unused-vars
  onStateChange: (state: AssetState) => void;

  onBrowse: () => void;
}

const AddAssetsOptions: React.FC<AddAssetsOptionProps> = ({
  style,
  state,
  source,
  onStateChange,
  onBrowse
}) => {

  const { trackAssetSourceSelected } = useAssetAdoption();

  const cleverstoryIntegrationEnabled = useSelector((state: any) => {
    const integrated = _.get(state, "accounts.item.cleverstoryIntegration");
    const workspaces = _.get(state, "cleverStory.workspaces");

    return integrated && _.size(workspaces) > 0;
  });

  const handleExistingAssetsClicked = () => {
    onStateChange({
      ...state,
      option: "hub-assets",
    });

    if (source) {
      trackAssetSourceSelected({
        ...source,
        source: 'existing_assets'
      });
    }
  };

  const handleAddFromCleverStoryClicked = () => {
    onStateChange({
      ...state,
      option: "cleverstory-assets",
    });

    if (source) {
      trackAssetSourceSelected({
        ...source,
        source: 'cleverstory'
      });
    }
  };

  const handleBrowse = () => {
    if (source) {
      trackAssetSourceSelected({
        ...source,
        source: 'file'
      });
    }

    if (onBrowse) {
      onBrowse();
    }
  }

  const handleLinkCreated = () => {
    if (source) {
      trackAssetSourceSelected({
        ...source,
        source: 'link'
      });
    }
  }

  return (
    <animated.div className={styles.options} style={style}>
      <div
        className={cx(styles.option, styles.addFromHub)}
        onClick={handleExistingAssetsClicked}
      >
        <img
          src="/images/assets/add/add_from_hub_collections.svg"
          alt="Streams & collections"
        />
        <div className={styles.details}>
          <h6 className={styles.title}>Streams & collections</h6>
          <span className={styles.description}>
            Choose your favourite assets from within Paperflite. No judgements here
          </span>
          <div className={styles.actions}>
            <Button
              variant="tertiary"
              className={styles.action}
              icon="paperflite-add"
              text="Add"
            />
          </div>
        </div>
      </div>
      <div className={styles.divider} />
      <div className={styles.option}>
        <img src="/images/assets/add/add_from_file.svg" alt="Drag & drop" />
        <div className={styles.details}>
          <h6 className={styles.title}>Personal drives</h6>
          <span className={styles.description}>
            Got something better on your device? Drag and drop it here.
          </span>
          <div className={styles.actions}>
            <Button
              variant="tertiary"
              className={styles.action}
              icon="paperflite-add"
              text="Browse"
              onClick={handleBrowse}
            />
          </div>
        </div>
      </div>

      {cleverstoryIntegrationEnabled && (
        <>
          <div className={styles.divider} />
          <div
            className={cx(styles.option, styles.addFromCleverStory)}
            onClick={handleAddFromCleverStoryClicked}
          >
            <img
              src="/images/assets/add/add_from_cleverstory.svg"
              alt="add from cleverstory"
            />
            <div className={styles.details}>
              <img
                height={24}
                width={95}
                src="/images/assets/add/cleverstory.png"
                alt="cleverstory"
              />
              <span className={styles.description}>
                Select assets from Cleverstory. Because everyone loves a good story.
              </span>
              <div className={styles.actions}>
                <Button
                  variant="tertiary"
                  className={styles.action}
                  icon="paperflite-add"
                  text="Add"
                />
              </div>
            </div>
          </div>
        </>
      )}

      <div className={styles.divider} />
      <div className={styles.option}>
        <img src="/images/assets/add/add_from_url.svg" alt="Add content URL" />
        <div className={styles.details}>
          <h6 className={styles.title}>Add content URL</h6>
          <AddUrlAsset onCreate={handleLinkCreated} />
        </div>
      </div>
    </animated.div>
  );
};

interface AssetSelectionProps {
  style: any;
  state: AssetState;
  // eslint-disable-next-line no-unused-vars
  onStateChange: (state: AssetState) => void;
}

const AssetSelection: React.FC<AssetSelectionProps> = ({
  state,
  onStateChange
}) => {
  const dispatch = useDispatch();

  const assets = state.entities || [];

  const handleRemove = (asset: Asset | AssetUpload) => {
    if (asset.entityType === "asset-upload") {
      const uploader = FileUploadService.getUploader(asset.id);

      if (uploader) {
        console.log(`Aborting upload ${asset.id}`);
        uploader.abortUpload();
      }
      dispatch(invalidateAssetUpload(asset.id));
    }

    if (asset.entityType === "link") {
      dispatch(invalidateLinkAsset(asset.id));
    }

    const assets = state.entities.filter((entity) => entity.id !== asset.id);
    onStateChange({
      ...state,
      entities: assets
    });
  }

  return (
    <animated.div className={styles.entitySelectionContainer}>
      <div className={styles.entityHeader}>
        <h6 className={styles.title}>{_.size(assets)} Assets</h6>
      </div>
      <div className={styles.entities}>
        {assets.map(asset => {
          const entityType = _.get(asset, 'entityType');

          if (entityType === 'asset-upload') {
            return (
              <div className={styles.asset} key={asset.id}>
                <AssetUploadCard asset={asset as AssetUpload} onRemove={handleRemove} />
              </div>
            );
          } else if (entityType === 'asset') {
            return (
              <div className={styles.asset} key={asset.id}>
                <AssetCard asset={asset as Asset} editable={true} onRemove={handleRemove} />
              </div>
            );
          } else if (entityType === 'link') {
            return (
              <div className={styles.asset} key={asset.id}>
                <LinkAssetCard url={asset.url} onRemove={handleRemove} linkAsset={asset} />
              </div>
            );
          } else if (entityType === 'cleverstory') {
            return (
              <div className={styles.asset} key={asset.id}>
                <CleverstoryAssetCard page={asset.item} />
              </div>
            );
          }
        })}
      </div>
    </animated.div>
  );
};

const FileDropIndicator: React.FC = () => {
  return (
    <div className={styles.dragDrop}>
      <div className={styles.dragDropContent}>
        <img src="/images/assets/add/drag_drop.svg" alt="" />
        <p className={styles.message}>Drop files here to upload</p>
      </div>
    </div>
  );
}

export { AddAssetsDialog, AddAssetsPanel, AddAssets, FileDropIndicator, AssetSelection };
